import { useRef, useEffect, useState, useCallback } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { FactoryModels } from './pages/FactoryModels/FactoryModels'
import ResetPassword from './pages/ResetPassword/ResetPassword'
import { ErrorLayer } from './components/ErrorLayer/ErrorLayer'
import { startApplication } from './api/startup'
import useDidUpdateEffect from './utils/useDidUpdateEffect'
import { TextGetterV2, TextId, useTextsV2 } from './utils/useTexts'
import useDetectMouseCapabilities from './utils/useDetectMouseCapabilities'
import {
  deleteEysStartConfigId,
  getAndDeleteSessionIdFromSessionStorage,
  setSessionIdInSessionStorage,
} from './utils/sessionStorageUtil'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingMopinionFeedbackSent,
  ScaniaAdobeTrackingMopinionHidden,
  ScaniaAdobeTrackingMopinionNext,
  ScaniaAdobeTrackingMopinionReady,
  ScaniaAdobeTrackingMopinionRedirect,
  ScaniaAdobeTrackingMopinionShown,
  ScaniaAdobeTrackingMopinionWillHide,
  ScaniaAdobeTrackingMopinionWillShow,
  ScaniaAdobeTrackingClickEysSendChangesEvent,
  ScaniaAdobeTrackingSessionTimeEvent,
} from './utils/adobeAnalytics'
import { ModalConsequenceOfChange } from './components/Modal/ModalConsequenceOfChange'
import { ModalChangesLost } from './components/Modal/ModalChangesLost'
import { ModalUploadXml } from './components/Modal/ModalUploadXml'
import { ModalError } from './components/Modal/ModalError'
import { ModalRfqSender } from './components/Modal/ModalRfqSender'
import {
  buildRelativeAllViewPage,
  buildRelativeGvPage,
  buildRelativeLandingPageUrl,
  buildRelativeOverviewPage,
  buildRelativeRfqPage,
} from './utils/UrlBuilders'
import { LinkLandingPage } from './pages/LinkLandingPage/LinkLandingPage'
import { useClient } from './utils/useClient'
import { ModalSendChangesToEys } from './components/Modal/ModalSendChangesToEys'
import {
  CocOpenedFromType,
  ExtendedCheckStatus,
  GuidedViewPageType,
  MarketLanguageState,
  SidePanelContent,
  SmartDashCampaignMode,
} from './store/types'
import {
  NewTruckLoadedHandler,
  NewTruckLoadedInfo,
  NewTruckLoadedInfoRedux,
  TruckLoadedSource,
} from './types/TruckLoadedTypes'
import { SESSION_FAILURE } from './api/errors'
import { RfqParams, UserConfigInfo } from './api/generated'
import { ModalUserAccountGdprConsent } from './components/Modal/ModalUserAccountGdprConsent'
import { buildBackendUrl, buildClient } from './api/clientStartupUtils'
import { GuidedOfferingClientStateStatus } from './types/GuidedOffering/GuidedOfferingClientState'
import ApplicationPage from './pages/GuidedOffering/B_ApplicationPage/ApplicationPage'
import { MultiLayoutPage } from './pages/GuidedOffering/C_MultiLayoutPage/MultiLayoutPage'
import OptionalsPage from './pages/GuidedOffering/D_OptionalsPage/OptionalsPage'
import {
  GuidedOfferingExitResultType,
  exitGuidedOfferingAndLoadConfigIntoBuildMode,
  stepThroughGuidedOfferingToEnd,
} from './api/guidedOffering'
import { ModalWaitingForExtendedCheck } from './components/Modal/ModalWaitingForExtendedCheck'
import { LandingHome } from './pages/LandingHome/LandingHome'
import { GoFmMode } from './api/goFmMode'
import { useAppDispatch, useAppSelector } from './store/hooks'
import { urlHasBevParam } from './pages/GuidedOffering/urlHasBevParam'
import { getUrlParametersToString } from './utils/getUrlParametersToString'
import { OperationsPage } from './pages/GuidedOffering/A_OperationsPage/OperationsPage'
import {
  closeGvPanel,
  openGvPanel,
  getSidePanelState,
  setGvSidePanelContent,
} from './store/sidePanelSlice'
import {
  getFactoryModelsMode,
  getInitialMarketLanguageState,
  getMarketLanguageState,
  getMarketSettingsState,
  setInitData,
} from './store/appSlice'
import {
  getExtendedCheckStatus,
  getGarageListState,
  getGuidedOfferingClientState,
  getPreloaderState,
  getIsLoggedInState,
  getIsLoadedTruck,
  getSessionInitDataState,
  startHideInitialLoadingScreen,
  setError,
  setExtendedCheckState,
  setGuidedOfferingStateStatus,
  setInitState,
  setRfqInput,
  setSessionId,
  setLoadingScreenIsHidden,
  getStartupData,
  getSessionState,
  setSmartDashCampaignMode,
  setUserSelectionTimestamp,
  getUserSelectionTimestamp,
  setSeriesFrame,
  getSeriesFrame,
  getErrorState,
  getGuidedViewPage,
  getSummaryOrRfqOpenedFromPage,
  setRfqOrEysButtonEnabled,
  setGuidedViewPage,
  setSummaryOrRfqOpenedFromPage,
  setIsLoadedTruck,
  setShowFullPageViewer,
} from './store/sessionDataSlice'
import {
  ModalConsequenceOfChangeProps,
  closeModalChangesLost,
  getModalState,
  openModalChangesLost,
  openModalSendChangesToEys,
  setErrorIsFatal,
  setErrorModalState,
  setModalRfqSendingState,
} from './store/modalSlice'
import { getTruckInfoState, setTruckInfo } from './store/truckInfoSlice'
import {
  ConsequeceOfChangeUserChoice,
  ConsequenceOfChangeDialogData,
  ConsequenceOfChangeHandler,
} from './types/ConsequenceOfChangeTypes'
import { LoadingTruck } from './pages/GuidedOffering/LoadingTruck'
import { styled } from 'styled-components'
import { GetConsultationPage } from './pages/GuidedView/RfqPage/GetConsultationPage'
import { Preloader } from './pages/Preloader/Preloader'
import { SidePanel } from './components/SidePanels/SidePanel'
import { PanoramaViewer } from './pages/GuidedView/MainImage/PanoramaViewer'
import { FullPageImageViewer } from './pages/GuidedView/FullPageImageViewer'
import { AppHeader } from './components/Header/Header'
import { MainMenuMobileTds } from './pages/GuidedView/MainMenuMobile/MainMenuMobileTds'
import { isTabletOrSmaller } from './utils/screenQuery'
import { SummaryPage } from './pages/GuidedView/SummaryPage/SummaryPage'
import {
  closeMainMenuMobile,
  getMenuState,
  setMenuTreeAllView,
  setMenuTreeGv,
} from './store/menuSlice'
import { isMenuId } from './utils/isMenuId'
import { PopUpSaveshareMenu } from './pages/GuidedView/PopUpSaveShareMenu'
import { OverviewPage } from './pages/GuidedView/OverviewPage/OverviewPage'
import { GuidedViewPage } from './pages/GuidedView/GuidedViewPage/GuidedViewPage'
import { AllConfigurationsPage } from './pages/GuidedView/AllOptionsPage/AllViewPage'

const PageRoot = styled.div`
  height: 100%;
  min-height: 100%;
  width: 100vw;
  min-width: 100vw;
  background-color: var(--tds-white);
  display: flex;
  flex-direction: column;
`

declare global {
  interface Window {
    /** Provided for Mopinion triggers. */
    languageIso: string | undefined

    /** Provided for Mopinion triggers. */
    etelMarket: string | undefined

    /** Provided for Mopinion triggers. */
    etelLanguage: string | undefined
  }
}

// TODO: Investigate and delete this as part of 9.0. Why was it added??
//       Had to remove this when moving this GUI application to the root path
//       "/" to replace the old GUI.
// First check "go" is the first subdirectory.
//if (!document.location.pathname.split('/').includes('go')) {
//  const slowDownRiscOfHorribleLoop = 5000
//  console.log(`%c Reloading with subpath /go in 5 sec `, 'background: red;')
//  setTimeout(() => (document.location.href = '/'), slowDownRiscOfHorribleLoop)
//}

export interface ExternalIntegrationLoaders {
  initializeMopinion: VoidFunction
}

interface ActionWaitingForLogin {
  run: () => void
}

interface AppProps {
  externalLoaders: ExternalIntegrationLoaders
}

function App(props: AppProps): JSX.Element {
  const dispatch = useAppDispatch()
  const client = useClient()
  const t2 = useTextsV2()
  const loading = useRef(false)
  useSetDocumentTitle(t2)
  const navigate = useNavigate()

  const sessionInitData = useAppSelector(getSessionInitDataState)
  const startHidePreloader =
    useAppSelector(getPreloaderState).startHidePreloader
  const preloaderIsHidden = useAppSelector(getPreloaderState).preloaderIsHidden
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const initialMarketLanguage = useAppSelector(getInitialMarketLanguageState)
  const isLoggedIn = useAppSelector(getIsLoggedInState)
  const marketSettings = useAppSelector(getMarketSettingsState)
  const factoryModelsMode = useAppSelector(getFactoryModelsMode)
  const startupData = useAppSelector(getStartupData)
  const extendedCheckState = useAppSelector(getExtendedCheckStatus)
  const isLoadedTruck = useAppSelector(getIsLoadedTruck)
  const garageList = useAppSelector(getGarageListState)
  const goClientState = useAppSelector(getGuidedOfferingClientState)
  const showFullPageViewer = useAppSelector(getSessionState).showFullPageViewer
  const showPanoramaViewer = useAppSelector(getSessionState).showPanoramaViewer
  const {
    modalSendChangesToEysIsOpen,
    modalChangesLostIsOpen,
    modalUploadXmlIsOpen,
    modalUserAccountGdprConsentIsOpen,
    modalRfqSendingState,
    errorModalState,
    errorIsFatal,
  } = useAppSelector(getModalState)
  const mainMenuIsOpen = useAppSelector(getMenuState).mainMenuMobileIsOpen
  const sidePanelState = useAppSelector(getSidePanelState)
  const popUpMenuShareSaveIsOpen =
    useAppSelector(getMenuState).menuSaveSharePopUpIsOpen
  const isFirstCheckSavedInGarage = useRef(true)
  const [modalConsequenceOfChangeProps, setModalConsequenceOfChangeProps] =
    useState<ModalConsequenceOfChangeProps | null>(null)
  const [preparingJumpToBuildMode, setPreparingJumpToBuildMode] =
    useState(false)
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const highlightTimerId = useRef<number | null>(null)
  const actionWaitingForLogin = useRef<ActionWaitingForLogin | null>(null)
  const suppressNativePageReloadWarning = useRef<boolean>(false)
  const timeSessionLoaded = Date.now()
  const seriesFrame = useAppSelector(getSeriesFrame)
  const hasError = useAppSelector(getErrorState)
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const summaryOrRfqOpenedFromPage = useAppSelector(
    getSummaryOrRfqOpenedFromPage,
  )
  const preloadDone = useRef(false)
  const preloaderRef = useRef<HTMLDivElement | null>(null)
  const interiorMenuIdAllview =
    useAppSelector(getMenuState).menuResultAllView?.interiorMenuIdAllView
  const oneTrustEnabled =
    useAppSelector(getStartupData)?.settings.oneTrustEnabled

  // Intended for triggering hook updates based on configuration changes.
  // The configuration itself is too large to report to the client, so we just
  // keep this counter in combination with the session id to represent a unique
  // configuration state.
  //
  // TODO: When the old GUI is retired, refactor the OutBop and rename it to
  // ConfigChangeResponse or something like that. Store that new refactored
  // response object instead of userSelectionCount.
  // EDIT: We have started to refactor OutBop now and it's stored in this
  // component as latestConfigChangeResult.
  const userSelectionTimestamp = useAppSelector(getUserSelectionTimestamp)

  const [rfqSentWithTimestamp, setRfqSentWithTimestamp] = useState<Date | null>(
    null,
  )
  const [eysChangesSentWithTimestamp, setEysChangesSentWithTimestamp] =
    useState<Date | null>(null)
  const functionalCookiesAllowed = useRef<boolean | null>(null)
  const performanceCookiesAllowed = useRef<boolean | null>(null)

  // TODO: Merge with bop etc. and rename to SessionConfigurationInfo?
  const truckInfo = useAppSelector(getTruckInfoState)

  // Update html meta data and initialize the cookie handling using OneTrust
  useEffect(() => {
    if (!marketLanguage) {
      return
    }
    document.documentElement.lang =
      marketLanguage.isoLocalization === 'tw-TW'
        ? 'zh-TW'
        : marketLanguage.isoLocalization
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', t2(TextId.WINDOW_DESCRIPTION))
    document
      .querySelector('meta[name="keywords"]')
      ?.setAttribute('content', t2(TextId.WINDOW_KEYWORDS))
    if (oneTrustEnabled) {
      const script = document.createElement('script')
      script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
      script.type = 'text/javascript'
      script.charset = 'UTF-8'
      script.setAttribute('data-document-language', 'true')
      script.setAttribute(
        'data-domain-script',
        '9d78e14a-5af5-4fc7-8d13-be82ad0bce2f',
      )
      document.body.appendChild(script)
    }
  }, [marketLanguage, t2, oneTrustEnabled])

  const checkCookies = () => {
    if (typeof window.OptanonActiveGroups === 'undefined') {
      return { performanceAllowed: null, functionalAllowed: null }
    }
    const activeGroups = window.OptanonActiveGroups.split(',')
    const performanceAllowed = activeGroups.includes('C0002')
    const functionalAllowed = activeGroups.includes('C0003')

    return { performanceAllowed, functionalAllowed }
  }

  const loadPermittedCookieConjuringFeatures = useCallback(() => {
    if (performanceCookiesAllowed.current === true) {
      console.log('Performance cookies are allowed.')
    }
    if (functionalCookiesAllowed.current === true) {
      console.log('Functional cookies are allowed.')

      // TODO: Revisit this if statement, this function should probably not be
      // called before marketSettings and marketLanguage are defined? Take them
      // as non-nullable parameters instead.
      if (marketSettings && marketLanguage) {
        const mop = marketSettings.mopinionSettings.find(
          (s) => s.languageIso === marketLanguage.isoLanguageCode,
        )
        if (mop?.mopinionEnabled) {
          props.externalLoaders.initializeMopinion()
        }
      }
    }
  }, [marketLanguage, marketSettings, props.externalLoaders])

  // OneTrust cookie handling.
  //
  // See OneTrust in the README.md file for testing instructions.
  //
  useEffect(() => {
    const waitForOneTrust = () => {
      if (typeof window.OptanonActiveGroups === 'undefined') {
        setTimeout(waitForOneTrust, 100)
        return
      }

      const initialCookies = checkCookies()
      functionalCookiesAllowed.current = initialCookies.functionalAllowed
      performanceCookiesAllowed.current = initialCookies.performanceAllowed
      loadPermittedCookieConjuringFeatures()

      // Listener for consent changes, this will only be triggered after the
      // user selects something in the cookie popup. It will not happen if the
      // user has selected something during a previous visit or if the user
      // selected something when visiting a different part of scania.com.
      if (window.OneTrust?.OnConsentChanged) {
        window.OneTrust.OnConsentChanged(() => {
          const newCookies = checkCookies()
          const oldFunctionalCookiesAllowed = functionalCookiesAllowed.current
          const oldPerformanceCookiesAllowed = performanceCookiesAllowed.current
          functionalCookiesAllowed.current = newCookies.functionalAllowed
          performanceCookiesAllowed.current = newCookies.performanceAllowed

          if (
            oldFunctionalCookiesAllowed !== newCookies.functionalAllowed ||
            oldPerformanceCookiesAllowed !== newCookies.performanceAllowed
          ) {
            loadPermittedCookieConjuringFeatures()
          }
        })
      }
    }

    if (oneTrustEnabled) {
      waitForOneTrust()
    } else {
      loadPermittedCookieConjuringFeatures()
    }
  }, [loadPermittedCookieConjuringFeatures, oneTrustEnabled])

  const endPreloader = () => {
    if (!preloaderRef.current) {
      throw new Error('Expected preloader to be defined.')
    }
    if (preloaderIsHidden) {
      return
    }

    preloaderRef.current.ontransitionend = (ev) => {
      if (preloaderRef.current) {
        preloaderRef.current.ontransitionend = null
      }

      dispatch(setLoadingScreenIsHidden())
    }
  }

  const exitFromOptionalsPageAndJumpToBuildMode = () => {
    exitAndJumpToBuildMode(true)
  }

  const handleFatalError = useCallback(
    (message?: string) => {
      dispatch(setErrorIsFatal(true))
      dispatch(setErrorModalState(message || t2(TextId.ERROR_FATAL)))
      dispatch(startHideInitialLoadingScreen())
    },
    [dispatch, t2],
  )

  const handleSessionFailure = useCallback(() => {
    const message = t2(TextId.ERROR_SESSION)
    if (errorIsFatal) {
      console.error(message)
      return
    }
    dispatch(setErrorModalState(message))
    dispatch(startHideInitialLoadingScreen())
  }, [dispatch, errorIsFatal, t2])

  const handleNewTruckIsLoaded: NewTruckLoadedHandler = useCallback(
    (info: NewTruckLoadedInfo) => {
      const infoToRedux = { ...info, timeLoaded: info.timeLoaded.toISOString() }
      dispatch(setTruckInfo(infoToRedux))
      dispatch(setUserSelectionTimestamp(info.timeLoaded.getTime()))
      if (info.source === TruckLoadedSource.EXPLORE_YOUR_SCANIA_START_CONFIG) {
        // The EYS start config is considered to be in sync with the EYS
        // database and the "Send changes" button should be disabled.
        setEysChangesSentWithTimestamp(info.timeLoaded)
      }
      dispatch(closeGvPanel())
      dispatch(setGvSidePanelContent(null))
    },
    [dispatch],
  )

  const exitAndJumpToBuildMode = useCallback(
    async (isOptionalsPageExit?: boolean) => {
      if (loading.current) {
        return
      }
      if (!marketLanguage) {
        return
      }
      if (!client) {
        return
      }
      if (goClientState?.status !== GuidedOfferingClientStateStatus.Active) {
        return
      }
      const sessionId = sessionInitData?.sessionId
      if (!sessionId) {
        return
      }
      loading.current = true
      setPreparingJumpToBuildMode(true)
      dispatch(
        setGuidedOfferingStateStatus(
          GuidedOfferingClientStateStatus.WaitingForBuildMode,
        ),
      )
      try {
        let newGoInfo = goClientState.lastResponse
        if (!isOptionalsPageExit) {
          newGoInfo = await stepThroughGuidedOfferingToEnd(client, newGoInfo)
        }
        const loadResult = await exitGuidedOfferingAndLoadConfigIntoBuildMode(
          client,
          sessionId,
          newGoInfo,
        )
        switch (loadResult.type) {
          case GuidedOfferingExitResultType.Success:
            break
          case GuidedOfferingExitResultType.ChangeStepError:
            handleFatalError(t2(TextId.ERROR_SECM_CHANGE_STEP))
            return
          case GuidedOfferingExitResultType.SessionFailure:
          case GuidedOfferingExitResultType.UnknownError:
          default:
            throw new Error(
              'Failed to load Guided Offering configuration into Build Mode.',
            )
        }
        if (!loadResult.successData?.mainMenus) {
          console.error('Expected mainMenus to be defined.')
          handleFatalError()
          return
        }
        const info: NewTruckLoadedInfo = {
          mainMenus: loadResult.successData.mainMenus,
          factoryModelName: null,
          isFactoryModel: false,
          publicConfigId: null,
          savedAsId: null,
          savedAsName: null,
          source: TruckLoadedSource.GUIDED_OFFERING,
          timeLoaded: new Date(),
        }
        handleNewTruckIsLoaded(info)
        const relativeUrl = buildRelativeOverviewPage(marketLanguage)
        const params = relativeUrl.params
        let link = relativeUrl.path + getUrlParametersToString(params)
        setPreparingJumpToBuildMode(false)
        loading.current = false
        replaceHistoryBeforeJump(t2, marketLanguage)
        navigate(link)
      } catch (err) {
        console.error(err)
        handleFatalError(t2(TextId.ERROR_SERVICE_UNAVAILABLE))
        return
      }
    },
    [
      client,
      handleFatalError,
      navigate,
      dispatch,
      goClientState,
      marketLanguage,
      handleNewTruckIsLoaded,
      sessionInitData,
      t2,
    ],
  )

  const isPublicConfigGuid = (s: string) => {
    let exampleGuid = '0f8fad5b-d9cb-469f-a165-70867728950e'
    const regex = new RegExp(
      '^[0-9A-Fa-f]{8}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{12}$',
    )
    return s.length === exampleGuid.length && regex.test(s)
  }

  const handleTimelineJumpToBuildMode = useCallback(async () => {
    if (!marketLanguage) {
      return
    }
    try {
      // Changed as part of SC1M-1256.
      const pubCfgId = t2(TextId.SKIP_TO_BUILD_MODE_CONFIG_ID).trim()
      if (isPublicConfigGuid(pubCfgId) && !urlHasBevParam()) {
        console.log('Using custom configuration ')
        if (loading.current) {
          console.log('Already loading, ignoring click.')
          return
        }
        loading.current = true
        setPreparingJumpToBuildMode(true)
        const etelMarket = marketLanguage.market
        const etelLanguage = marketLanguage.language
        const sessionId = sessionInitData?.sessionId
        if (!sessionId) {
          throw new Error('Expected sessionId to be defined.')
        }
        if (!etelMarket) {
          throw new Error('Expected etelMarket to be defined.')
        }
        if (!etelLanguage) {
          throw new Error('Expected etelLanguage to be defined.')
        }
        if (!client) {
          throw new Error('Expected client to be defined.')
        }
        const publicConfigLoadResult = await client.publicConfigV2Load(
          sessionId,
          pubCfgId,
          true,
        )
        if (publicConfigLoadResult.error || !publicConfigLoadResult.success) {
          console.error(
            'Failed to load public config, error: ' +
              publicConfigLoadResult.error,
          )
          handleFatalError()
          return
        }
        // TODO: Handle the response, display the consequence of change dialog
        // when needed, but with only the OK button, it's not possible to
        // rollback the configuration here.
        const coc = await client.getInitialConsequenceOfChange({
          sessionId,
        })
        if (!coc.getInitialConsequenceOfChange) {
          if (coc.error === SESSION_FAILURE) {
            handleSessionFailure()
            return
          }
          handleFatalError()
          return
        }
        const info: NewTruckLoadedInfo = {
          mainMenus: publicConfigLoadResult.success.mainMenus,
          factoryModelName: coc.factoryModelName || null,
          isFactoryModel: coc.isFactoryModel || false,
          publicConfigId: pubCfgId,
          savedAsId: null,
          savedAsName: null,
          source: TruckLoadedSource.PUBLIC_CONFIG,
          timeLoaded: new Date(),
        }
        handleNewTruckIsLoaded(info)
        const relativeUrl = buildRelativeOverviewPage(marketLanguage)
        const params = relativeUrl.params
        let link = relativeUrl.path + getUrlParametersToString(params)
        setPreparingJumpToBuildMode(false)
        loading.current = false
        replaceHistoryBeforeJump(t2, marketLanguage)
        navigate(link)
      } else {
        exitAndJumpToBuildMode()
      }
    } catch (err) {
      console.error(err)
      handleFatalError()
    }
  }, [
    client,
    exitAndJumpToBuildMode,
    handleFatalError,
    handleNewTruckIsLoaded,
    handleSessionFailure,
    navigate,
    marketLanguage,
    sessionInitData,
    t2,
  ])

  useDetectMouseCapabilities()

  // Handle session heartbeat.
  useEffect(() => {
    if (!client) {
      return
    }
    const sessionId = sessionInitData?.sessionId
    if (!sessionId) {
      return
    }
    const id = setInterval(async () => {
      try {
        await client.heartbeat(sessionId)
      } catch {
        clearInterval(id)
        handleSessionFailure()
      }
    }, 30 * 1000) // 30 seconds, same as the old GUI.
    return () => {
      clearInterval(id)
    }
  }, [client, handleSessionFailure, sessionInitData])

  // Get startup data, store startup data.
  const doStartupOnce = useRef(false)
  useEffect(() => {
    async function doAsyncStuff() {
      doStartupOnce.current = true
      try {
        const storedSessionId = getAndDeleteSessionIdFromSessionStorage()
        if (!initialMarketLanguage) {
          throw new Error('Expected initialMarketLanguage to be defined.')
        }
        const backendUrl = buildBackendUrl()
        const client = buildClient(backendUrl)

        // Try to access a protected path to see if we need to trigger a login.
        const startLoginUrl =
          backendUrl +
          '/api/start_login?originalUrl=' +
          encodeURIComponent(window.location.href)
        const testAuthUrl = backendUrl + '/api/build_info'
        try {
          const response = await fetch(testAuthUrl)
          if (response.redirected || !response.ok) {
            throw new Error('Try to login.')
          }
        } catch {
          // Interpret any error as a need to login, the request above should
          // never fail for other reasons. TODO: Detect other errors (network
          // errors can always happen, or bugs). Navigate the web browser tab to
          // a protected resource and allow the tab to be redirected to the
          // login.
          window.location.href = startLoginUrl
          return
        }

        const startAPIResponse = await startApplication(
          backendUrl,
          client,
          initialMarketLanguage,
          window.tempStartupCriticalState.appData.show_preview_texts,
          storedSessionId ?? undefined,
          window.tempStartupCriticalState.appData.sessionTimeoutSeconds,
        )
        const languageInfo = startAPIResponse.marketSettings.languages.find(
          (l) => l.etelLanguage === initialMarketLanguage.language,
        )
        if (!languageInfo) {
          throw new Error('Expected languageInfo to be defined.')
        }
        let smartDashCampaignOverride: SmartDashCampaignMode | null = null
        if (
          window.tempStartupCriticalState.appData.smartDashCampaignVersion ===
          'LINES_AND_CURVE'
        ) {
          smartDashCampaignOverride =
            SmartDashCampaignMode.FancyAnimatedLinesAndCurve
        }
        let newSmartDashCampaignMode = SmartDashCampaignMode.None
        if (startAPIResponse.marketSettings.smartDashCampaignEnabled) {
          newSmartDashCampaignMode =
            smartDashCampaignOverride === null
              ? SmartDashCampaignMode.FancyAnimatedLinesAndCurve
              : smartDashCampaignOverride
        }
        dispatch(setSmartDashCampaignMode(newSmartDashCampaignMode))
        window.languageIso = languageInfo.isoCode
        window.etelMarket = initialMarketLanguage.market.toFixed(0)
        window.etelLanguage = initialMarketLanguage.language.toFixed(0)
        registerMopinionEventListeners()
        dispatch(setInitData(startAPIResponse))
        dispatch(setInitState(startAPIResponse))
        dispatch(setSessionId(startAPIResponse.sessionInitData.sessionId))
      } catch (err) {
        dispatch(startHideInitialLoadingScreen())
        console.log('Cought a custom error:', err)
        dispatch(
          setError({
            errorLevel: 2,
            friendlyErrorMessage: t2(TextId.ERROR_FRIENDLY_SERVER_DISCONNECTED),
            //errorMessage: `Could not connect to Scania servers.`,
            errorMessage: ('' + err).slice(0, 300),
          }),
        )
      }
    }
    if (!doStartupOnce.current) doAsyncStuff()
  }, [dispatch, initialMarketLanguage, t2])
  // End: Get session.

  useDidUpdateEffect(() => {
    if (startHidePreloader && !preloadDone.current) {
      endPreloader()
      preloadDone.current = true
    }
  }, [startHidePreloader])

  const handleBack = () => {
    // TODO: If we move the BrowserRouter to index.js in the ReactDOM.render
    // block, it may be possible to interact with the router api here instead of
    // window.history. Going back with the browser back button should behave in
    // the same way though so this should be correct.
    //
    // TODO: The above TODO is almost done! Try to use the useHistory part of
    // React Router instead of window!
    window.history.back()
  }

  // Bridge the Consequence of Change modal dialog interaction through a Promise
  // to allow using `await` for the dialog user input.
  const handleConsequenceOfChange: ConsequenceOfChangeHandler = useCallback(
    (
      data: ConsequenceOfChangeDialogData,
      cocOpenedFrom: CocOpenedFromType,
    ): Promise<ConsequeceOfChangeUserChoice> => {
      return new Promise((resolve) => {
        let handleChoice = (choice: ConsequeceOfChangeUserChoice): void => {
          resolve(choice)
        }
        const props: ModalConsequenceOfChangeProps = {
          data,
          handleChoice,
          cocOpenedFrom,
        }
        setModalConsequenceOfChangeProps(props)
      })
    },
    [],
  )

  // TODO: Revisit this later.
  useEffect(() => {
    if (!marketSettings?.garageEnabled) {
      dispatch(setIsLoadedTruck(false))
      return
    }
    if (factoryModelsMode === GoFmMode.OnlyFactoryModels) {
      dispatch(setIsLoadedTruck(false))
      return
    }
    if (truckInfo?.isFactoryModel) {
      dispatch(setIsLoadedTruck(false))
      return
    }
    if (!isLoggedIn) {
      dispatch(setIsLoadedTruck(false))
      return
    }
    if (!truckInfo?.savedAsId) {
      dispatch(setIsLoadedTruck(false))
      return
    }
    dispatch(setIsLoadedTruck(true))
  }, [
    factoryModelsMode,
    marketSettings,
    truckInfo,
    userSelectionTimestamp,
    isLoggedIn,
    dispatch,
  ])

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const trackingEvent: ScaniaAdobeTrackingSessionTimeEvent = {
        event: ScaniaAdobeEventId.SessionTimeSeconds,
        eventInfo: {
          timeSpentInSession: (Date.now() - timeSessionLoaded) / 1000,
        },
      }
      pushAdobeEvent(trackingEvent)

      if (suppressNativePageReloadWarning.current) {
        // Used for the New button since that one will pop a custom dialog
        // instead of the browser built-in warning.
        return
      }
      const truckInfoDate = convertReduxStateToTruckInfo(truckInfo)

      if (
        userSelectionTimestamp?.getTime() ===
        truckInfoDate?.timeLoaded.getTime()
      ) {
        // No changes have been applied to the truck.
        return
      }

      if (guidedViewPage === null) {
        return
      }
      e.preventDefault()
      // Legacy support
      e.returnValue = t2(TextId.WARNING_LEAVING_PAGE)

      return t2(TextId.WARNING_LEAVING_PAGE)
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [t2, userSelectionTimestamp, truckInfo, guidedViewPage, timeSessionLoaded])

  const handleNewClick = useCallback(() => {
    const truckInfoDate = convertReduxStateToTruckInfo(truckInfo)

    // We have a custom dialog for this warning in this case.
    suppressNativePageReloadWarning.current = true

    // garage enabled, not FM, changes but truck is not saved
    if (
      marketSettings?.garageEnabled &&
      factoryModelsMode !== GoFmMode.OnlyFactoryModels &&
      !truckInfo?.isFactoryModel &&
      userSelectionTimestamp?.getTime() !== truckInfoDate?.timeLoaded.getTime()
    ) {
      dispatch(openModalChangesLost())
      return
    }

    // saved truck with changes
    if (
      isLoadedTruck &&
      userSelectionTimestamp?.getTime() !== truckInfoDate?.timeLoaded.getTime()
    ) {
      dispatch(openModalChangesLost())
      return
    }

    // saved truck with no changes or garage not enabled
    if (!marketLanguage) {
      throw new Error('Expected marketLanguage to be defined.')
    }
    if (!sessionInitData) {
      throw new Error('Expected sessionInitData to be defined.')
    }
    deleteEysStartConfigId()
    if (isLoggedIn) {
      setSessionIdInSessionStorage(sessionInitData.sessionId)
    }
    const relativeUrl = buildRelativeLandingPageUrl(marketLanguage)
    const params = relativeUrl.params
    const restartUrl = relativeUrl.path + getUrlParametersToString(params)
    console.log('Reload')
    document.location.href = restartUrl
    return
  }, [
    marketLanguage,
    factoryModelsMode,
    isLoggedIn,
    marketSettings,
    sessionInitData,
    dispatch,
    truckInfo,
    userSelectionTimestamp,
    isLoadedTruck,
  ])

  const handleSaveAsFromNew = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      dispatch(closeModalChangesLost())
      if (isLoggedIn) {
        dispatch(openGvPanel())
        dispatch(setGvSidePanelContent(SidePanelContent.save))
      } else {
        actionWaitingForLogin.current = {
          run: () => {
            dispatch(openGvPanel())
            dispatch(setGvSidePanelContent(SidePanelContent.save))
          },
        }
        dispatch(openGvPanel())
        dispatch(setGvSidePanelContent(SidePanelContent.login))
      }
    },
    [dispatch, isLoggedIn],
  )

  const openRfqPage = useCallback(() => {
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    const asyncWrapper = async () => {
      const sessionId = sessionInitData?.sessionId
      if (!client) {
        return
      }
      if (!sessionId) {
        return
      }
      dispatch(setExtendedCheckState(ExtendedCheckStatus.None))
      if (guidedViewPage !== GuidedViewPageType.summary) {
        dispatch(setSummaryOrRfqOpenedFromPage(guidedViewPage))
      }
      const relativeUrl = buildRelativeRfqPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)

      try {
        dispatch(setExtendedCheckState(ExtendedCheckStatus.WaitingForResult))
        const prepResult = await client.prepareRfq(sessionId)
        const phase = prepResult.extendedCheckSuccess
          ? ExtendedCheckStatus.Success
          : ExtendedCheckStatus.Failure
        dispatch(setExtendedCheckState(phase))
      } catch (e) {
        console.error(e)
        handleFatalError()
        return
      }
    }
    asyncWrapper()
  }, [
    client,
    handleFatalError,
    sessionInitData,
    dispatch,
    marketLanguage,
    navigate,
    guidedViewPage,
  ])

  const openSendChangesToEysModal = useCallback(() => {
    const ev: ScaniaAdobeTrackingClickEysSendChangesEvent = {
      event: ScaniaAdobeEventId.EysSendChangesClick,
    }
    pushAdobeEvent(ev)
    dispatch(openModalSendChangesToEys())
  }, [dispatch])

  const handleSendChangesToEysSuccess = useCallback(() => {
    setEysChangesSentWithTimestamp(userSelectionTimestamp)
  }, [userSelectionTimestamp])

  const handleSendRfq = useCallback(
    (rfq: RfqParams) => {
      if (!marketLanguage) {
        throw new Error('Expected market language.')
      }
      dispatch(setModalRfqSendingState(rfq))
      const relativeUrl =
        summaryOrRfqOpenedFromPage === GuidedViewPageType.allView
          ? buildRelativeAllViewPage(marketLanguage)
          : summaryOrRfqOpenedFromPage === GuidedViewPageType.guidedView
          ? buildRelativeGvPage(marketLanguage)
          : buildRelativeOverviewPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    },
    [dispatch, marketLanguage, navigate, summaryOrRfqOpenedFromPage],
  )

  const handleRfqOrEysClick = useCallback(() => {
    if (!startupData) {
      return
    }
    if (startupData.exploreYourScaniaMode) {
      openSendChangesToEysModal()
    } else {
      openRfqPage()
    }
    dispatch(setShowFullPageViewer(false))
  }, [startupData, openSendChangesToEysModal, openRfqPage, dispatch])

  useEffect(() => {
    /*
    if (!sidePanelLogInIsOpen) {
      actionWaitingForLogin.current = null
      return
    }
      */
    if (!actionWaitingForLogin.current) {
      return
    }
    if (!isLoggedIn) {
      return
    }
    console.log('Running deferred login action.')
    const action = actionWaitingForLogin.current
    actionWaitingForLogin.current = null
    action.run()
  }, [isLoggedIn])

  // TODO: Move marketDenomination to a new state object together with
  // userSelectionTimestamp.

  const handleTruckHasBeenDeleted = useCallback(
    (truck: UserConfigInfo) => {
      if (!truckInfo) {
        dispatch(setTruckInfo(null))
      } else if (truckInfo?.savedAsId === truck.id) {
        const result = { ...truckInfo }
        result.savedAsId = null
        result.savedAsName = null
        dispatch(setTruckInfo(result))
      }
    },
    [truckInfo, dispatch],
  )

  // Reset check if the truck is already saved in garage if logged out
  useEffect(() => {
    if (!isFirstCheckSavedInGarage.current) {
      return
    }
    if (!isLoggedIn) {
      if (!truckInfo) {
        dispatch(setTruckInfo(null))
      } else {
        const result = { ...truckInfo, savedAsId: null, savedAsName: null }
        dispatch(setTruckInfo(result))
      }
      isFirstCheckSavedInGarage.current = false
    }
    if (isLoggedIn) {
      isFirstCheckSavedInGarage.current = true
    }
  }, [garageList, isLoggedIn, truckInfo, dispatch])

  const handleRfqSuccess = useCallback(() => {
    setRfqSentWithTimestamp(userSelectionTimestamp)
    dispatch(setRfqInput(null))
  }, [userSelectionTimestamp, dispatch])

  const handleMenuClick = useCallback(
    (menuId: string, gvPageToJumpTo: GuidedViewPageType) => {
      if (!sessionInitData?.sessionId) {
        return
      }
      if (!client) {
        return
      }
      const asyncWrapper = async () => {
        const isAllConfigPage = gvPageToJumpTo === GuidedViewPageType.allView
        const newMenuState = await client.exploreMenu({
          sessionId: sessionInitData.sessionId,
          menuId: menuId,
          showAllConfigMenu: isAllConfigPage,
        })
        if (newMenuState.success) {
          if (isAllConfigPage) {
            dispatch(setMenuTreeAllView(newMenuState.success))
          } else {
            dispatch(setMenuTreeGv(newMenuState.success))
          }

          const interiorMenuId = isAllConfigPage
            ? interiorMenuIdAllview
            : 'MENU_GV_CAB_INTERIOR'

          if (
            newMenuState.success.menuTree?.subMenus &&
            interiorMenuId &&
            newMenuState.success.menuTree?.subMenus.find(
              (menu) => menu.id === interiorMenuId,
            )?.isOpen
          ) {
            if (seriesFrame.seriesId !== 'INTERIOR') {
              dispatch(setSeriesFrame({ seriesId: 'INTERIOR', frameId: 1 }))
            }
          } else if (seriesFrame.seriesId === 'INTERIOR') {
            dispatch(setSeriesFrame({ seriesId: 'EXTERIOR', frameId: 2 }))
          }
          return newMenuState.success
        }
      }
      asyncWrapper()
    },
    [client, dispatch, sessionInitData, seriesFrame, interiorMenuIdAllview],
  )

  // Handle navigation and highlight choice for:
  // 1. Click on search result
  // 2. Click in ActiveChanges
  // 3. Click on Campaign link

  // TODO: Declare a named interface type for the function parameters.
  const navigateToMenu = useCallback(
    async (
      id: string,
      menuPath: string | null | undefined,
      gvPageToJumpTo: GuidedViewPageType,
    ): Promise<void> => {
      if (!sessionInitData) {
        throw new Error('Expected sessionInitData to be defined.')
      }
      if (!marketLanguage) {
        throw new Error('Expected marketLanguage to be defined.')
      }
      if (!client) {
        throw new Error('Expected client to be defined.')
      }

      var menuIdToClick = id
      if (!isMenuId(id)) {
        if (!menuPath) {
          menuIdToClick = 'SUBMENU_' + id.split('~')[0]
        } else {
          menuIdToClick = menuPath?.split('/').slice(-1)[0]
        }
      }

      if (isTabletOrSmaller()) {
        dispatch(closeGvPanel())
        dispatch(closeMainMenuMobile())
      }

      handleMenuClick(menuIdToClick, gvPageToJumpTo)
      setHighlightedId(id)

      const resetHighlightedId = () => {
        setHighlightedId(null)
      }

      if (highlightTimerId.current !== null) {
        window.clearTimeout(highlightTimerId.current)
      }
      highlightTimerId.current = window.setTimeout(resetHighlightedId, 3000)
    },
    [client, dispatch, handleMenuClick, marketLanguage, sessionInitData],
  )

  // TODO: Separate RFQ and EYS buttons.
  useEffect(() => {
    if (!startupData) {
      return
    }
    if (startupData.exploreYourScaniaMode) {
      // Make EYS send changes button be deactivated when loading new truck
      if (!eysChangesSentWithTimestamp?.getTime()) {
        setEysChangesSentWithTimestamp(userSelectionTimestamp)
        dispatch(setRfqOrEysButtonEnabled(true))
      } else {
        dispatch(
          setRfqOrEysButtonEnabled(
            eysChangesSentWithTimestamp?.getTime() !==
              userSelectionTimestamp?.getTime(),
          ),
        )
      }
      return
    }
    if (startupData.marketSettings.rfqEnabled) {
      dispatch(
        setRfqOrEysButtonEnabled(
          rfqSentWithTimestamp?.getTime() !== userSelectionTimestamp?.getTime(),
        ),
      )
      return
    }
    dispatch(setRfqOrEysButtonEnabled(false))
  }, [
    eysChangesSentWithTimestamp,
    rfqSentWithTimestamp,
    startupData,
    userSelectionTimestamp,
    dispatch,
  ])

  useEffect(() => {
    const pathname = window.location.pathname
    const guidedViewPage = pathname.includes('overview')
      ? GuidedViewPageType.overview
      : pathname.includes('gv')
      ? GuidedViewPageType.guidedView
      : pathname.includes('allView')
      ? GuidedViewPageType.allView
      : pathname.includes('summary')
      ? GuidedViewPageType.summary
      : pathname.includes('consultation')
      ? GuidedViewPageType.rfq
      : null
    dispatch(setGuidedViewPage(guidedViewPage))
  }, [dispatch, navigate])

  const handleSetSuppressNativePageReloadWarning = (suppress: boolean) => {
    suppressNativePageReloadWarning.current = suppress
  }

  return (
    <PageRoot data-name="App">
      <AppHeader />
      <Routes>
        <Route path="/" element={<LandingHome />} />
        <Route
          path="/link/:market/:language/:pubc"
          element={
            <LinkLandingPage
              handleFatalError={handleFatalError}
              handleNewTruckIsLoaded={handleNewTruckIsLoaded}
              handleSessionFailure={handleSessionFailure}
            />
          }
        />
        <Route
          path="/reset-password/:market/:language"
          element={<ResetPassword />}
        />
        <Route
          path="/factory-models/:market/:language"
          element={
            <FactoryModels
              handleFatalError={handleFatalError}
              handleNewTruckIsLoaded={handleNewTruckIsLoaded}
              handleSessionFailure={handleFatalError}
            />
          }
        />
        <Route
          path="/go/go-start/:market/:language"
          element={
            <OperationsPage
              handleGoBack={handleBack}
              handleTimelineSkip={handleTimelineJumpToBuildMode}
            />
          }
        />
        <Route
          path="/go/grid/:market/:language/:desiredselection"
          element={
            <ApplicationPage
              handleFatalError={handleFatalError}
              handleGoBack={handleBack}
              handleTimelineSkip={handleTimelineJumpToBuildMode}
            />
          }
        />
        <Route
          path="/go/slider/:market/:language/:desiredselection"
          element={
            <MultiLayoutPage
              handleFatalError={handleFatalError}
              handleGoBack={handleBack}
              handleTimelineSkip={handleTimelineJumpToBuildMode}
            />
          }
        />
        <Route
          path="/go/options/:market/:language/:desiredselection"
          element={
            <OptionalsPage
              handleFatalError={handleFatalError}
              handleGoBack={handleBack}
              handleJumpToBuildMode={exitFromOptionalsPageAndJumpToBuildMode}
              handleTimelineSkip={exitFromOptionalsPageAndJumpToBuildMode}
            />
          }
        />
        <Route
          path="/overview/:market/:language"
          element={
            <OverviewPage
              handleNewClick={handleNewClick}
              navigateToMenu={navigateToMenu}
              handleRfqOrEysClick={handleRfqOrEysClick}
              handleSessionFailure={handleSessionFailure}
              handleFatalError={handleFatalError}
            />
          }
        />
        <Route
          path="/gv/:market/:language"
          element={
            <GuidedViewPage
              handleNewClick={handleNewClick}
              handleRfqOrEysClick={handleRfqOrEysClick}
              handleFatalError={handleFatalError}
              handleSessionFailure={handleSessionFailure}
              navigateToMenu={navigateToMenu}
              handleCloseModalConsequenseOfChange={() =>
                setModalConsequenceOfChangeProps(null)
              }
              handleConsequenceOfChange={handleConsequenceOfChange}
              highlightedId={highlightedId}
            />
          }
        />
        <Route
          path="/allView/:market/:language"
          element={
            <AllConfigurationsPage
              handleNewClick={handleNewClick}
              handleRfqOrEysClick={handleRfqOrEysClick}
              navigateToMenu={navigateToMenu}
              handleCloseModalConsequenseOfChange={() =>
                setModalConsequenceOfChangeProps(null)
              }
              handleConsequenceOfChange={handleConsequenceOfChange}
              handleFatalError={handleFatalError}
              handleSessionFailure={handleSessionFailure}
              highlightedId={highlightedId}
            />
          }
        />
        <Route
          path="/summary/:market/:language"
          element={
            <SummaryPage
              handleFatalError={handleFatalError}
              handleSessionFailure={handleSessionFailure}
              handleRfqOrEysClick={handleRfqOrEysClick}
            />
          }
        />
        <Route
          path="/consultation/:market/:language"
          element={
            <GetConsultationPage
              handleRfqSendClick={handleSendRfq}
              handleFatalError={handleFatalError}
              handleSessionFailure={handleSessionFailure}
            />
          }
        />
      </Routes>
      {popUpMenuShareSaveIsOpen && <PopUpSaveshareMenu />}
      {modalSendChangesToEysIsOpen && (
        <ModalSendChangesToEys
          handleSuccess={handleSendChangesToEysSuccess}
          data-name="ModalPopUpEys"
        />
      )}
      {guidedViewPage !== GuidedViewPageType.rfq &&
        extendedCheckState === ExtendedCheckStatus.WaitingForResult && (
          <ModalWaitingForExtendedCheck data-name="ModalWaitingForExtendedCheck" />
        )}
      {modalRfqSendingState &&
        (extendedCheckState === ExtendedCheckStatus.Success ||
          extendedCheckState === ExtendedCheckStatus.Failure) && (
          <ModalRfqSender
            handleSuccess={handleRfqSuccess}
            data-name="ModalRfqSentConfirmation"
          />
        )}
      {modalConsequenceOfChangeProps ? (
        <ModalConsequenceOfChange
          data-name="ModalConsequenceOfChange"
          data={modalConsequenceOfChangeProps.data}
          handleChoice={modalConsequenceOfChangeProps.handleChoice}
          cocOpenedFrom={modalConsequenceOfChangeProps.cocOpenedFrom}
        />
      ) : null}
      {modalChangesLostIsOpen && (
        <ModalChangesLost
          handleSaveAsClick={handleSaveAsFromNew}
          handleSetSuppressNativePageReloadWarning={
            handleSetSuppressNativePageReloadWarning
          }
          data-name="ModalChangesLost"
        />
      )}
      {modalUploadXmlIsOpen && marketLanguage && (
        <ModalUploadXml
          data-name="ModalUploadXml"
          handleFatalError={handleFatalError}
          handleNewTruckIsLoaded={handleNewTruckIsLoaded}
          handleSessionFailure={handleSessionFailure}
          handleConsequenceOfChange={handleConsequenceOfChange}
          handleCloseModalConsequenseOfChange={() =>
            setModalConsequenceOfChangeProps(null)
          }
        />
      )}
      {modalUserAccountGdprConsentIsOpen && (
        <ModalUserAccountGdprConsent
          handleFatalError={handleFatalError}
          handleSessionFailure={handleSessionFailure}
          data-name="ModalUserAccountGdprConsent"
        />
      )}
      {errorModalState !== null && <ModalError data-name="ModalErrorXml" />}
      {showPanoramaViewer && <PanoramaViewer />}
      {showFullPageViewer && (
        <FullPageImageViewer
          handleRfqOrEysClick={handleRfqOrEysClick}
          navigateToMenu={navigateToMenu}
        />
      )}
      {sidePanelState.gvSidePanelOpen && (
        <SidePanel
          handleFatalError={handleFatalError}
          handleSessionFailure={handleSessionFailure}
          handleNewTruckIsLoaded={handleNewTruckIsLoaded}
          handleConsequenceOfChange={handleConsequenceOfChange}
          handleCloseModalConsequenseOfChange={() =>
            setModalConsequenceOfChangeProps(null)
          }
          handleTruckHasBeenDeleted={handleTruckHasBeenDeleted}
          navigateToMenu={navigateToMenu}
          handleRfqOrEysClick={handleRfqOrEysClick}
        />
      )}
      {mainMenuIsOpen && (
        <MainMenuMobileTds
          data-name="MainMenu"
          handleNewClick={handleNewClick}
          handleSessionFailure={handleSessionFailure}
          navigateToMenu={navigateToMenu}
        />
      )}
      {hasError.errorLevel >= 2 && <ErrorLayer />}
      {preparingJumpToBuildMode && <LoadingTruck />}
      {!preloaderIsHidden && <Preloader ref={preloaderRef} />}
    </PageRoot>
  )
}

export default App

// Date is stored as string in Redux
export function convertReduxStateToTruckInfo(
  truckInfoRedux: NewTruckLoadedInfoRedux | null,
): NewTruckLoadedInfo | null {
  if (!truckInfoRedux) {
    return null
  }
  return {
    ...truckInfoRedux,
    timeLoaded: new Date(truckInfoRedux.timeLoaded), // Convert string to Date
  }
}

// TODO: Revisit later, still needed after the partial rewrite of GO?
function replaceHistoryBeforeJump(
  t2: TextGetterV2,
  marketLanguage: MarketLanguageState,
) {
  const url = `/?etel_market=${marketLanguage.market}&etel_language=${marketLanguage.language}`
  // https://bugzilla.mozilla.org/show_bug.cgi?id=753264
  window.history.replaceState(
    { cause: 'JUMP_TO_BUILD_MODE' },
    `${t2(TextId.WINDOW_TITLE)} - Guided Offering`,
    url,
  )
}

function useSetDocumentTitle(t2: TextGetterV2) {
  useEffect(() => {
    window.document.title = t2(TextId.WINDOW_TITLE)
  }, [t2])
}

/**
 * https://mopinion.atlassian.net/wiki/spaces/KB/pages/509313085/How+do+I+use+Mopinion+events
 **/
function registerMopinionEventListeners() {
  document.addEventListener('mopinion_feedback_sent', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionFeedbackSent = {
      event: ScaniaAdobeEventId.MopinionFeedbackSent,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
  document.addEventListener('mopinion_hidden', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionHidden = {
      event: ScaniaAdobeEventId.MopinionHidden,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
  document.addEventListener('mopinion_next', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionNext = {
      event: ScaniaAdobeEventId.MopinionNext,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
  document.addEventListener('mopinion_ready', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionReady = {
      event: ScaniaAdobeEventId.MopinionReady,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
  document.addEventListener('mopinion_redirect', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionRedirect = {
      event: ScaniaAdobeEventId.MopinionRedirect,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
  document.addEventListener('mopinion_shown', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionShown = {
      event: ScaniaAdobeEventId.MopinionShown,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
  document.addEventListener('mopinion_will_hide', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionWillHide = {
      event: ScaniaAdobeEventId.MopinionWillHide,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
  document.addEventListener('mopinion_will_show', function (e: any) {
    const adobeEvent: ScaniaAdobeTrackingMopinionWillShow = {
      event: ScaniaAdobeEventId.MopinionWillShow,
      eventInfo: e?.detail,
    }
    pushAdobeEvent(adobeEvent)
  })
}
